/**
 * Control Activities Service
 * 
 * Handles all control activities related API calls
 */

import { mapRiskRanking, RiskRanking, SyncfusionSearchParam } from './types';
import { apiRequest } from './apiClient';
import { parseCommaSeparated } from '../utils/stringUtils';

// ========================================
// Types
// ========================================

export type ControlStatus = 'Draft' | 'Active';
export type ControlCategory = 'Financial' | 'Operating' | 'Regulatory';
export type ControlType = 'Preventative' | 'Detective' | 'Other';
export type FrequencyType = 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Annually' | 'Ongoing';
export type KeyNonKey = 'Key' | 'Non-Key';
export type TestingScope = 'In Scope' | 'Out of Scope';
export type YesNoNotStarted = 'Yes' | 'No' | 'WIP';
export type WIPStatus = 'Not Started' | 'WIP' | 'Yes';
export type FinalCompletedStatus = 'Not Started' | 'WIP' | 'Completed';

export interface ControlDescription {
  what: string;
  when: string;
  who: string;
  why: string;
  how: string;
  evidence: string;
}

export interface TestingAttachment {
  id: string;
  name: string;
  type: 'PDF' | 'Doc' | 'File';
}

export interface TestingDetails {
  testPlan: string;
  walkthroughResult: string;
  designEffective: YesNoNotStarted;
  totalTestSamples: number;
  samplesTested: number;
  attachments: TestingAttachment[];
  testingStatus: number | null; // percentage, null means N/A
  interimCompleted: WIPStatus;
  finalCompleted: FinalCompletedStatus;
  operatingEffectiveness: YesNoNotStarted;
  completionDate: string | null;
  auditorAssigned: {
    id: string;
    name: string;
    avatar?: string;
  } | null;
  budgetHoursToTest: number | null;
}

export interface ControlActivity {
  id: string;
  status: ControlStatus;
  cycle: {
    id: string;
    code: string;
    name: string;
  };
  process: {
    id: string;
    code: string;
    name: string;
  };
  controlCode: string;
  riskAssessmentId: string;
  controlDescription: ControlDescription;
  controlCategory: ControlCategory;
  controlType: ControlType;
  frequency: FrequencyType;
  keyOrNonKey: KeyNonKey;
  riskRanking: RiskRanking;
  controlOwner: string;
  processOwner: {
    id: string;
    name: string;
    avatar?: string;
  };
  applications: {
    id: string;
    name: string;
  }[];
  assertions: string[];
  objectiveIds: string[];
  objectiveNumbers: string; // Raw format from backend: "01~02"
  objectiveProcessCodes?: string; // Raw format from backend: "A01~A01"
  objectiveDisplayTitles: string[]; // Computed array for display: ["A01.01", "A01.02"]
  isGapCompensating: boolean;
  testingScope: boolean;
  testing: TestingDetails | null;
  effectivenessGapId: string | null;
  controlWeight: number | null;
}

// ========================================
// Backend Response Types
// ========================================

interface ControlActivityBackend {
  Id: string;
  ControlStatus: string;
  ControlStatusRaw: number;
  CycleId: string;
  CycleCode: string;
  ProcessId: string;
  ProcessCode: string;
  ControlCode: string;
  RiskAssessmentId: string;
  What: string;
  When: string;
  Who: string;
  Why: string;
  How: string;
  Evidence: string;
  ControlCategory: string;
  ControlType: string;
  Frequency: string;
  FrequencyRaw: number;
  IsKeyControl: boolean;
  RiskRanking: string;
  RiskRatingRaw: number;
  ControlOwner: string;
  ProcessOwnerId: string;
  ProcessOwner: string;
  ApplicationIds: string;
  ApplicationNames: string;
  IsInTestingScope: boolean;
  TestPlan: string;
  Walkthrough: string;
  DesignEffective: boolean | null;
  IsGapCompensating: boolean;
  TotalSamples: number;
  TestedSamples: number;
  DownloadIds: string;
  DownloadNames: string;
  TestingStatus: number;
  InterimCompleted: string;
  InterimCompletedRaw: number;
  FinalCompleted: string;
  FinalCompletedRaw: number;
  OperatingEffectiveness: boolean | null;
  CompletionDate: string;
  AuditorId: string;
  AuditorName: string;
  BudgetHours: number;
  AssertionIds: string;
  AssertionNames: string;
  ObjectiveIds: string;
  ObjectiveNumbers: string;
  ObjectiveProcessCodes: string;
  ControlWeight: number;
  CreatedAt: string;
  EffectivenessGapId: string | null;
}

interface GetControlActivitiesBackendResponse {
  result: ControlActivityBackend[];
  count: number;
}

// ========================================
// Mapping Functions
// ========================================

/**
 * Map backend ControlActivityBackend to frontend ControlActivity
 */
const mapBackendToControlActivity = (backend: ControlActivityBackend): ControlActivity => {
  // Parse comma-separated strings to arrays

  // Parse applications
  const applicationIds = parseCommaSeparated(backend.ApplicationIds || '');
  const applicationNames = parseCommaSeparated(backend.ApplicationNames);
  const applications = applicationIds.map((id, index) => ({
    id,
    name: applicationNames[index] || '',
  }));

  // Parse assertions
  const assertions = parseCommaSeparated(backend.AssertionNames);

  // Parse objective IDs
  const objectiveIds = parseCommaSeparated(backend.ObjectiveIds);
  const objectiveNumbers = backend.ObjectiveNumbers || '';
  const objectiveProcessCodes = backend.ObjectiveProcessCodes || '';
  
  // Create display titles array by combining process codes with objective numbers
  // Format: "ProcessCode.ObjectiveNumber" (e.g., "A01.01", "A01.02")
  const objectiveNumbersArray = objectiveNumbers.split('~').filter(n => n.trim());
  const objectiveProcessCodesArray = objectiveProcessCodes.split('~').filter(n => n.trim());
  const objectiveDisplayTitles = objectiveNumbersArray.map((num, index) => {
    const processCode = objectiveProcessCodesArray[index] || '';
    return processCode ? `${processCode}.${num.trim()}` : num.trim();
  });

  // Parse attachments
  const downloadIds = parseCommaSeparated(backend.DownloadIds);
  const downloadNames = parseCommaSeparated(backend.DownloadNames);
  const attachments: TestingAttachment[] = downloadIds.map((id, index) => ({
    id,
    name: downloadNames[index] || 'Unknown',
    type: 'File' as const,
  }));

  const mapEffectiveness = (val: boolean | null): YesNoNotStarted => {
    if (val === true) return 'Yes';
    if (val === false) return 'No';
    return 'WIP';
  };

  // Map interim completed status
  const mapInterimCompleted = (status: string): WIPStatus => {
    const normalized = status.toLowerCase();
    if (normalized === 'notstarted') return 'Not Started';
    if (normalized === 'wip') return 'WIP';
    if (normalized === 'yes') return 'Yes';
    return 'Not Started';
  };

  // Map final completed status
  const mapFinalCompleted = (status: string): FinalCompletedStatus => {
    const normalized = status.toLowerCase();
    if (normalized === 'notstarted') return 'Not Started';
    if (normalized === 'wip') return 'WIP';
    if (normalized === 'completed') return 'Completed';
    return 'Not Started';
  };

  // Build testing details if in scope
  const testing: TestingDetails | null = backend.IsInTestingScope ? {
    testPlan: backend.TestPlan || '',
    walkthroughResult: backend.Walkthrough || '',
    designEffective: mapEffectiveness(backend.DesignEffective),
    totalTestSamples: backend.TotalSamples || 0,
    samplesTested: backend.TestedSamples || 0,
    attachments,
    testingStatus: backend.TestingStatus,
    interimCompleted: mapInterimCompleted(backend.InterimCompleted),
    finalCompleted: mapFinalCompleted(backend.FinalCompleted),
    operatingEffectiveness: mapEffectiveness(backend.OperatingEffectiveness),
    completionDate: backend.CompletionDate || null,
    auditorAssigned: backend.AuditorId ? {
      id: backend.AuditorId,
      name: backend.AuditorName || 'Unknown',
      avatar: undefined,
    } : null,
    budgetHoursToTest: backend.BudgetHours || null,
  } : null;


  return {
    id: backend.Id,
    status: backend.ControlStatus as ControlStatus,
    cycle: {
      id: backend.CycleId,
      code: backend.CycleCode,
      name: '', // Not provided by backend
    },
    process: {
      id: backend.ProcessId,
      code: backend.ProcessCode,
      name: '', // Not provided by backend
    },
    controlCode: backend.ControlCode,
    riskAssessmentId: backend.RiskAssessmentId,
    controlDescription: {
      what: backend.What,
      when: backend.When,
      who: backend.Who,
      why: backend.Why,
      how: backend.How,
      evidence: backend.Evidence,
    },
    controlCategory: backend.ControlCategory as ControlCategory,
    controlType: backend.ControlType as ControlType,
    frequency: backend.Frequency as FrequencyType,
    keyOrNonKey: backend.IsKeyControl ? 'Key' : 'Non-Key',
    riskRanking: mapRiskRanking(backend.RiskRanking),
    controlOwner: backend.ControlOwner,
    processOwner: {
      id: backend.ProcessOwnerId,
      name: backend.ProcessOwner || 'Unknown',
      avatar: undefined,
    },
    applications,
    assertions,
    objectiveIds,
    objectiveNumbers,
    objectiveProcessCodes,
    objectiveDisplayTitles,
    isGapCompensating: backend.IsGapCompensating,
    testingScope: backend.IsInTestingScope,
    testing,
    effectivenessGapId: backend.EffectivenessGapId || null,
    controlWeight: backend.ControlWeight ?? null,
  };
};

export type EffectivenessStatus = 'effective' | 'ineffective' | 'untested' | 'outofscope';

export interface GetControlActivitiesRequest {
  annualReportId: string;
  // Filter parameters (use "Raw" fields for enum filtering per BE guidance)
  status?: ControlStatus[];
  cycleIds?: string[];
  processIds?: string[];
  controlCategory?: ControlCategory[];
  controlType?: ControlType[];
  frequency?: FrequencyType[];
  isKeyControl?: boolean | null;
  riskRanking?: string[];
  processOwnerIds?: string[];
  testingScope?: boolean | null;
  designEffective?: (boolean | null)[];
  interimCompleted?: string[];
  finalCompleted?: string[];
  operatingEffectiveness?: (boolean | null)[];
  completionDate?: string;
  effectivenessStatus?: EffectivenessStatus[];
  hasAuditorAssigned?: boolean; // true = has auditor, false = no auditor assigned
  search?: SyncfusionSearchParam[];
  // Pagination & sorting
  skip?: number;
  take?: number;
  sorted?: { name: string; direction: 'ascending' | 'descending' }[];
}

export interface GetControlActivitiesResponse {
  data: ControlActivity[];
  totalCount: number;
}

// Mapping functions for Raw enum fields (backend uses numeric values)
const controlStatusToRaw = (status: string): number => {
  const map: Record<string, number> = { 'Draft': 0, 'Active': 1 };
  return map[status] ?? 0;
};

const controlCategoryToRaw = (category: string): number => {
  const map: Record<string, number> = { 'Financial': 0, 'Operating': 1, 'Regulatory': 2 };
  return map[category] ?? 0;
};

const controlTypeToRaw = (type: string): number => {
  const map: Record<string, number> = { 'Preventative': 0, 'Detective': 1, 'Other': 2 };
  return map[type] ?? 0;
};

const frequencyToRaw = (frequency: string): number => {
  const map: Record<string, number> = { 'Daily': 1, 'Weekly': 2, 'Monthly': 3, 'Quarterly': 4, 'Annually': 5 };
  return map[frequency] ?? 0;
};

const riskRankingToRaw = (ranking: string): number => {
  const map: Record<string, number> = { 'Unranked': 0, 'Low': 1, 'Med': 2, 'High': 3 };
  return map[ranking] ?? 0;
};

const interimCompletedToRaw = (status: string): number => {
  const map: Record<string, number> = { 'NotStarted': 0, 'WIP': 1 };
  return map[status] ?? 0;
};

const finalCompletedToRaw = (status: string): number => {
  const map: Record<string, number> = { 'NotStarted': 0, 'WIP': 1, 'Completed': 2 };
  return map[status] ?? 0;
};

// ========== FILTER BUILDING UTILITIES ==========
// New Syncfusion filter structure from BE:
// - OR conditions: Use nested "predicates" array with "condition": "or"
// - AND conditions: Separate objects in "where" array (default)

type FilterValue = string | boolean | number | null;

interface FilterCondition {
  field: string;
  operator: string;
  value: FilterValue;
  condition?: 'and' | 'or';
}

interface FilterPredicateGroup {
  predicates: FilterCondition[];
}

type WhereCondition = FilterCondition | FilterPredicateGroup;


/**
 * Get control activities for an annual report with optional filtering, sorting and pagination
 * @param request - Request parameters including filters, sorting, and pagination
 */
export const getControlActivities = async (
  request: GetControlActivitiesRequest
): Promise<GetControlActivitiesResponse> => {
  try {
    // Build filter conditions using new Syncfusion structure
    const {
      whereConditions,
      addMultiValueFilter,
      addRawMultiValueFilter,
      addSingleValueFilter,
      addBooleanArrayFilter,
    } = buildFilterConditions();

    // Status (use ControlStatusRaw with numeric values)
    addRawMultiValueFilter('ControlStatusRaw', request.status, controlStatusToRaw);
    
    // Cycle IDs
    addMultiValueFilter('CycleId', request.cycleIds);
    
    // Process IDs
    addMultiValueFilter('ProcessId', request.processIds);
    
    // Control Category (use ControlCategoryRaw with numeric values)
    addRawMultiValueFilter('ControlCategoryRaw', request.controlCategory, controlCategoryToRaw);
    
    // Control Type (use ControlTypeRaw with numeric values)
    addRawMultiValueFilter('ControlTypeRaw', request.controlType, controlTypeToRaw);
    
    // Frequency (use FrequencyRaw with numeric values)
    addRawMultiValueFilter('FrequencyRaw', request.frequency, frequencyToRaw);
    
    if (request.riskRanking?.includes('Unranked')) {
      request.riskRanking.push("null");
    }

    // Risk Ranking (use RiskRatingRaw with numeric values)
    addRawMultiValueFilter('RiskRatingRaw', request.riskRanking, riskRankingToRaw);

    // Process Owner (filter by ProcessOwnerId)
    addMultiValueFilter('ProcessOwnerId', request.processOwnerIds);

    // Drawer filter: Key or Non-Key (boolean, single value)
    addSingleValueFilter('IsKeyControl', request.isKeyControl);

    // Drawer filter: Testing Scope (boolean, single value)
    addSingleValueFilter('IsInTestingScope', request.testingScope);

    // Drawer filter: Design Effective (array of boolean | null)
    addBooleanArrayFilter('DesignEffective', request.designEffective);

    // Drawer filter: Interim Completed (use InterimCompletedRaw with numeric values)
    addRawMultiValueFilter('InterimCompletedRaw', request.interimCompleted, interimCompletedToRaw);

    // Drawer filter: Final Completed (use FinalCompletedRaw with numeric values)
    addRawMultiValueFilter('FinalCompletedRaw', request.finalCompleted, finalCompletedToRaw);

    // Drawer filter: Operating Effectiveness (array of boolean | null)
    addBooleanArrayFilter('OperatingEffectiveness', request.operatingEffectiveness);

    // Drawer filter: Completion Date (equal operator)
    addSingleValueFilter('CompletionDate', request.completionDate);

    addMultiValueFilter('EffectivenessStatus', request.effectivenessStatus);

    // Auditor Assigned filter (empty check)
    if (request.hasAuditorAssigned !== undefined && request.hasAuditorAssigned !== null) {
      whereConditions.push({
        field: 'AuditorId',
        operator: request.hasAuditorAssigned ? 'notequal' : 'equal',
        value: '',
      });
    }

    const requestBody: {
      skip: number;
      take: number;
      requiresCounts: boolean;
      sorted?: { name: string; direction: 'ascending' | 'descending' }[];
      where?: WhereCondition[];
      search?: SyncfusionSearchParam[];
    } = {
      skip: request.skip || 0,
      take: request.take || 1000,
      requiresCounts: true,
    };

    // Only include sorted param if provided (let BE decide default)
    if (request.sorted) {
      requestBody.sorted = request.sorted;
    }
    
    // Add where conditions if any
    if (whereConditions.length > 0) {
      requestBody.where = whereConditions;
    }
    
    // Add search param if provided
    if (request.search) {
      requestBody.search = request.search;
    }
    const response = await apiRequest<GetControlActivitiesBackendResponse, typeof requestBody>(
      'POST',
      `/controls/index-grid/${request.annualReportId}`,
      requestBody,
      true // Authentication required
    );

    // Map backend data to frontend format
    let mappedData = response.result.map(mapBackendToControlActivity);

    return {
      data: mappedData,
      totalCount: response.count,
    };
  } catch (error) {
    console.error('Failed to fetch control activities:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch control activities',
    };
  }
};

/**
 * Build filter conditions for Syncfusion grid API
 * - Multiple values for same field: grouped with OR using predicates
 * - Single values: direct filter condition
 * - Different fields: AND logic (default)
 */
const buildFilterConditions = () => {
  const whereConditions: WhereCondition[] = [];

  // Add filter for multiple string values (OR within same field)
  const addMultiValueFilter = (field: string, values: string[] | undefined) => {
    if (!values?.length) return;
    
    if (values.length === 1) {
      // Single value - direct condition
      whereConditions.push({
        field,
        operator: 'equal',
        value: values[0],
      });
    } else {
      // Multiple values - use predicates with OR
      whereConditions.push({
        predicates: values.map(value => ({
          field,
          operator: 'equal',
          value,
          condition: 'or' as const,
        })),
      });
    }
  };

  // Add filter for multiple numeric values using Raw mapping (OR within same field)
  const addRawMultiValueFilter = (field: string, values: string[] | undefined, toRawFn: (v: string) => number | null) => {
    if (!values?.length) return;
    
    if (values.length === 1) {
      // Single value - direct condition
      whereConditions.push({
        field,
        operator: 'equal',
        value: toRawFn(values[0]),
      });
    } else {
      // Multiple values - use predicates with OR
      whereConditions.push({
        predicates: values.map(value => ({
          field,
          operator: 'equal',
          value: value != "null" ? toRawFn(value) : null,
          condition: 'or' as const,
        })),
      });
    }
  };

  // Add filter for single boolean/value
  const addSingleValueFilter = (field: string, value: FilterValue | undefined) => {
    if (value === null || value === undefined) return;
    
    whereConditions.push({
      field,
      operator: 'equal',
      value,
    });
  };

  // Add filter for boolean array (OR within same field) - for Design/Operating Effectiveness
  const addBooleanArrayFilter = (field: string, values: (boolean | null)[] | undefined) => {
    if (!values?.length) return;
    
    // Filter out null values (handled by frontend)
    const booleanValues = values ?? [];
    if (!booleanValues.length) return;
    
    if (booleanValues.length === 1) {
      whereConditions.push({
        field,
        operator: 'equal',
        value: booleanValues[0],
      });
    } else {
      whereConditions.push({
        predicates: booleanValues.map(value => ({
          field,
          operator: 'equal',
          value,
          condition: 'or' as const,
        })),
      });
    }
  };

  return {
    whereConditions,
    addMultiValueFilter,
    addRawMultiValueFilter,
    addSingleValueFilter,
    addBooleanArrayFilter,
  };
};

// ========================================
// Create Control Activity API
// ========================================

export interface CreateControlRequest {
  weight: number;
  what: string;
  who: string;
  why: string;
  how: string;
  evidence: string;
  category: 'financial' | 'operational' | 'regulatory';
  controlType: 'preventative' | 'detective' | 'other';
  controlFrequency: 'multipleTimesPerDay' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'semiAnnual' | 'annual' | 'ongoing';
  isKeyControl: boolean;
  controlOwner: string;
  isGapCompensating: boolean;
  processId: string;
  inTestingScope: boolean;
  testPlan: string;
  walthroughResult: string;
  isDesignEffective: boolean | null;
  samplesTotal: number;
  samplesTested: number;
  documentIds: string[];
  objectiveIds: string[];
  applicationIds: string[];
  interimCompleted: 'notStarted' | 'wip' | 'yes';
  finalCompleted: 'notStarted' | 'wip' | 'completed';
  operatingEffectiveness: boolean | null;
  auditorId?: string;
  budgetHours?: number;
  autoGenerateGAPs?: boolean;
}

export interface ControlResponse {
  data: {
    controlId: string;
    effectivenessGapId: string | null;
    // Other fields from backend
  };
  code: string;
  message: string;
}

/**
 * Create a new control activity
 */
export const createControl = async (
  request: CreateControlRequest
): Promise<ControlResponse> => {
  try {
    const response = await apiRequest<ControlResponse, CreateControlRequest>(
      'POST',
      '/controls',
      request,
      true // Authentication required
    );
    return response;
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create control activity',
    };
  }
};

// ========================================
// Update Control Activity API
// ========================================

export interface UpdateControlRequest extends CreateControlRequest {
  controlId: string;
}

/** Map UI ControlCategory to API category string (Operating -> operational, not operating) */
export const controlCategoryToApiCategory = (
  category: ControlCategory
): CreateControlRequest['category'] => {
  const map: Record<ControlCategory, CreateControlRequest['category']> = {
    Financial: 'financial',
    Operating: 'operational',
    Regulatory: 'regulatory',
  };
  return map[category] ?? 'financial';
};

/** Map UI ControlType to API controlType */
export const controlTypeToApiType = (
  controlType: ControlType
): CreateControlRequest['controlType'] => {
  const map: Record<ControlType, CreateControlRequest['controlType']> = {
    Preventative: 'preventative',
    Detective: 'detective',
    Other: 'other',
  };
  return map[controlType] ?? 'preventative';
};

/**
 * Build UpdateControlRequest from a ControlActivity (e.g. after getControlActivities).
 * Use overrides to change weight and/or budget hours without re-fetching.
 */
export const controlActivityToUpdateRequest = (
  control: ControlActivity,
  overrides?: {
    weight?: number;
    budgetHours?: number;
    testPlan?: string;
    walthroughResult?: string;
  }
): UpdateControlRequest => {
  const testing = control.testing;
  const weight =
    overrides?.weight !== undefined
      ? overrides.weight
      : control.controlWeight ?? 0;
  const budgetHours =
    overrides?.budgetHours !== undefined
      ? overrides.budgetHours
      : testing?.budgetHoursToTest ?? undefined;

  return {
    controlId: control.id,
    weight,
    what: control.controlDescription.what,
    who: control.controlDescription.who,
    why: control.controlDescription.why,
    how: control.controlDescription.how,
    evidence: control.controlDescription.evidence,
    category: controlCategoryToApiCategory(control.controlCategory),
    controlType: controlTypeToApiType(control.controlType),
    controlFrequency: frequencyToApiEnum(control.frequency),
    isKeyControl: control.keyOrNonKey === 'Key',
    controlOwner: control.controlOwner,
    isGapCompensating: control.isGapCompensating,
    processId: control.process.id,
    inTestingScope: control.testingScope,
    testPlan: overrides?.testPlan ?? testing?.testPlan ?? '',
    walthroughResult:
      overrides?.walthroughResult ?? testing?.walkthroughResult ?? '',
    isDesignEffective: convertEffectiveness(testing?.designEffective),
    samplesTotal: testing?.totalTestSamples ?? 0,
    samplesTested: testing?.samplesTested ?? 0,
    documentIds: testing?.attachments?.map(att => att.id) ?? [],
    objectiveIds: control.objectiveIds ?? [],
    applicationIds: control.applications?.map(app => app.id) ?? [],
    interimCompleted: convertInterimCompleted(testing?.interimCompleted),
    finalCompleted: convertFinalCompleted(testing?.finalCompleted),
    operatingEffectiveness: convertEffectiveness(testing?.operatingEffectiveness),
    auditorId: testing?.auditorAssigned?.id,
    budgetHours: budgetHours === null || budgetHours === undefined ? undefined : budgetHours,
  };
};

/**
 * Update an existing control activity
 */
export const updateControl = async (
  request: UpdateControlRequest
): Promise<ControlResponse> => {
  // console.log('UpdateControlRequest', request);
  try {
    const response = await apiRequest<ControlResponse, CreateControlRequest>(
      'PUT',
      `/controls`,
      request,
      true // Authentication required
    );
    return response;
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update control activity',
    };
  }
};

/**
 * Delete Control Activity API
 */
export const deleteControl = async (id: string): Promise<void> => {
  try {
    await apiRequest(
      'DELETE',
      `/controls/${id}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to delete control activity',
    };
  }
};

// ========================================
// Search Cycles/Processes for Control Activities
// ========================================

// Helper function to convert frequency string to API enum
export const frequencyToApiEnum = (frequency: string): CreateControlRequest['controlFrequency'] => {
  const mapping: Record<string, CreateControlRequest['controlFrequency']> = {
    'Multiple times per day': 'multipleTimesPerDay',
    'Daily': 'daily',
    'Weekly': 'weekly',
    'Monthly': 'monthly',
    'Quarterly': 'quarterly',
    'Semi-Annual': 'semiAnnual',
    'Annual': 'annual',
    'Ongoing': 'ongoing'
  };
  return mapping[frequency] || 'daily';
};

// Convert effectiveness values - BE accepts null for "Not Started"
export const convertEffectiveness = (val: string | null | undefined): boolean | null => {
  if (val === 'Yes' || val === 'yes') return true;
  if (val === 'No' || val === 'no') return false;
  return null;
};

// Convert interim completed to API format
export const convertInterimCompleted = (val: string | undefined): 'notStarted' | 'wip' | 'yes' => {
  if (val === 'WIP' || val === 'wip') return 'wip';
  if (val === 'Yes' || val === 'yes' || val === 'Completed' || val === 'completed') return 'yes';
  return 'notStarted';
};

// Convert final completed to API format
export const convertFinalCompleted = (val: string | undefined): 'notStarted' | 'wip' | 'completed' => {
  if (val === 'WIP' || val === 'wip') return 'wip';
  if (val === 'Completed' || val === 'completed') return 'completed';
  return 'notStarted';
};

// ========================================
// Auditors API
// ========================================

export interface Auditor {
  id: string;
  name: string;
}

interface AuditorBackend {
  auditorId: string;
  auditorName: string;
}

interface GetAuditorsResponse {
  data: AuditorBackend[];
  code: string;
  message: string;
}

export interface GetAuditorsRequest {
  companyId: string;
  searchTerm?: string;
}

/**
 * Get list of auditors for a company
 * Can be used to filter results by searchTerm
 */
export const getAuditors = async (
  request: GetAuditorsRequest
): Promise<Auditor[]> => {
  try {
    // Build query parameters
    const params = new URLSearchParams();
    params.append('companyId', request.companyId);
    if (request.searchTerm) {
      params.append('searchTerm', request.searchTerm);
    }

    const response = await apiRequest<GetAuditorsResponse>(
      'GET',
      `/controls/auditors?${params.toString()}`,
      undefined,
      true // Authentication required
    );

    // Map backend data to frontend format
    return response.data.map(auditor => ({
      id: auditor.auditorId,
      name: auditor.auditorName,
    }));
  } catch (error) {
    console.error('Failed to fetch auditors:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch auditors',
    };
  }
};